import React, { useState, useEffect } from 'react';
import {
  Paper, TextField, Button, Typography, Box, MenuItem, Grid
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeService, positionService, seniorityLevelService } from '../services/api';
import DepartmentSelector from './DepartmentSelector';
import { usePermissions } from './PermissionsContext';

const EmployeeForm = ({ embedded=false, person=null, employee=null, disabled: disabledProp=false, onSave, onCancel }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeId = id;
  const { role } = usePermissions();

  // HRManager can only view, not edit (read-only mode)
  const isHRManagerReadOnly = role === 'HRManager';
  const disabled = disabledProp || isHRManagerReadOnly;

  const effectiveId = embedded ? (employee?.id ?? employee?.Id ?? null) : routeId;
  const isEdit = Boolean(effectiveId);

  const [formData, setFormData] = useState({
    personId: person?.Id ?? person?.id ?? null,
    email: '',
    positionId: '',
    seniorityLevelId: '',
    // HireDate removed - now derived from Contract.StartDate
    status: 'Active',
    departmentId: ''
  });

  const [positions, setPositions] = useState([]);
  const [seniorityLevels, setSeniorityLevels] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadLookupData();
    if (isEdit && !embedded) {
      loadEmployee();
    }
  }, [routeId, isEdit, embedded]);

  // Hydrate from prop when embedded
  useEffect(() => {
    if (embedded && employee) {
      const emp = employee;
      setFormData(prev => ({
        ...prev,
        personId: emp.personId ?? prev.personId ?? (emp.person?.id ?? emp.person?.Id ?? null),
        email: emp.email || '',
        positionId: emp.positionId || '',
        seniorityLevelId: emp.seniorityLevelId || '',
        departmentId: emp.departmentId ?? '',
        // HireDate removed - now derived from Contract.StartDate
        status: emp.status || emp.Status || 'Active'
      }));
    } else if (embedded && !employee && (person?.Id || person?.id)) {
      // New employee for selected person → ensure personId is set
      setFormData(prev => ({ ...prev, personId: person?.Id ?? person?.id }));
    }
  }, [embedded, employee, person]);

  const loadLookupData = async () => {
    try {
      const [positionsRes, levelsRes] = await Promise.all([
        positionService.getAll(),
        seniorityLevelService.getAll()
      ]);
      setPositions(positionsRes || []);
      setSeniorityLevels(levelsRes || []);
    } catch (error) {
      console.error('Error loading lookup data:', error);
      setPositions([]);
      setSeniorityLevels([]);
    }
  };

  const loadEmployee = async () => {
    try {
      const idToLoad = embedded ? (employee?.id ?? employee?.Id) : routeId;
      const emp = await employeeService.getById(idToLoad);
      setFormData(prev => ({
        ...prev,
        person: emp.person || { firstName: '', lastName: '' },
        email: emp.email || '',
        positionId: emp.positionId || '',
        seniorityLevelId: emp.seniorityLevelId || '',
        departmentId: emp.departmentId ?? ''
        // HireDate removed - now derived from Contract.StartDate
      }));
    } catch (error) {
      console.error('Error loading employee:', error);
    }
  };

  const handleChange = (e) => {
    if (disabled) return;
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExtractedData = (extractedEmployee) => {
    // HireDate removed - now derived from Contract.StartDate
    setFormData({
      ...formData,
      email: extractedEmployee.email || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const employeeData = {
        id: effectiveId ? parseInt(effectiveId) : undefined,
        personId: formData.personId ?? (person?.Id ?? person?.id ?? null),
        email: formData.email,
        positionId: parseInt(formData.positionId),
        seniorityLevelId: parseInt(formData.seniorityLevelId),
        departmentId: formData.departmentId ? parseInt(formData.departmentId) : null,
        // HireDate removed - now derived from Contract.StartDate
        status: formData.status
      };

      if (isEdit) {
        await employeeService.update(effectiveId, employeeData);
        if (embedded) onSave?.();
      } else {
        const created = await employeeService.create(employeeData);
        if (!embedded) navigate('/employees');
        else onSave?.(created);
      }
    } catch (error) {
      console.error('Error saving employee:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Row 1 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              disabled={disabled}
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              disabled={disabled}
              select
              label="Position"
              name="positionId"
              value={formData.positionId}
              onChange={handleChange}
              required
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              {positions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.title}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Row 2 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              disabled={disabled}
              select
              label="Seniority Level"
              name="seniorityLevelId"
              value={formData.seniorityLevelId}
              onChange={handleChange}
              required
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              {seniorityLevels.map((level) => (
                <MenuItem key={level.id} value={level.id}>
                  {level.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DepartmentSelector
              value={formData.departmentId}
              disabled={disabled}
              onChange={(v)=>setFormData(prev=>({...prev, departmentId: v||''}))}
              label="Department"
              placeholder="Choose department"
              size="small"
            />
          </Grid>

          {/* Row 3 - HireDate removed, now derived from Contract.StartDate */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              disabled={disabled}
              select
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="Terminated">Terminated</MenuItem>
              <MenuItem value="OnLeave">On Leave</MenuItem>
              <MenuItem value="OnMaternityLeave">On Maternity/Paternity Leave</MenuItem>
              <MenuItem value="OnSickLeave">On Sick Leave</MenuItem>
              <MenuItem value="Probation">Probation</MenuItem>
              <MenuItem value="Suspended">Suspended</MenuItem>
              <MenuItem value="NoticePeriod">Notice Period</MenuItem>
              <MenuItem value="Retired">Retired</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        {!disabled && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => { if (!embedded) navigate('/employees'); else onCancel?.(); }}
            >
              {isEdit ? 'Close' : 'Cancel'}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default EmployeeForm;



